import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  cacheProducts, getCachedProducts,
  savePendingOrder, getPendingOrders, clearPendingOrders
} from '../utils/offlineStorage';
import { useLang } from '../utils/LanguageContext';
import { useAuth } from '../utils/AuthContext';
import { t } from '../utils/translations';
import './OrderScreen.css';
import Loader from '../components/Loader';

const DEFAULT_CATEGORIES = ['Hot Drinks', 'Cold Drinks', 'Food'];

// The three original default categories stay translated; custom
// categories created by a shop are shown exactly as the shop wrote them.
function catLabel(cat, tr) {
  if (cat === 'Hot Drinks') return tr.catHotDrinks;
  if (cat === 'Cold Drinks') return tr.catColdDrinks;
  if (cat === 'Food') return tr.catFood;
  return cat;
}

function OrderScreen() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [category, setCategory] = useState(0);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [orderType, setOrderType] = useState('dine_in');
  const [tableLabel, setTableLabel] = useState('');
  const [payment, setPayment] = useState('cash');
  const [orderError, setOrderError] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [newCustomer, setNewCustomer] = useState('');

  const { lang } = useLang();
  const { org, user, isPro } = useAuth();
  const orgId = org?.id;
  const tr = t[lang];

  const orgCategories = org?.categories?.length ? org.categories : DEFAULT_CATEGORIES;
  const CATEGORIES = [tr.all, ...orgCategories.map(c => catLabel(c, tr))];

  const syncPendingOrders = useCallback(async () => {
    const pending = getPendingOrders(orgId);
    if (pending.length === 0) return;
    setSyncing(true);

    for (const { order, items } of pending) {
      try {
        // Insert the full order (type, table, payment, customer, cashier)
        const { data: savedOrder, error } = await supabase
          .from('orders')
          .insert([order])
          .select()
          .single();

        if (error || !savedOrder) continue;

        await supabase.from('order_items').insert(
          items.map(i => ({
            order_id: savedOrder.id,
            product_id: i.product_id,
            product_name: i.product_name,
            price: i.price,
            cost: i.cost || 0,
            quantity: i.quantity
          }))
        );

        for (const i of items) {
          await supabase.rpc('decrement_stock', {
            product_id: i.product_id,
            amount: i.quantity
          });
        }
      } catch (e) {
        console.error('Sync error:', e);
      }
    }

    clearPendingOrders(orgId);
    setPendingCount(0);
    setSyncing(false);
  }, [orgId]);

  async function fetchCustomers() {
    const { data } = await supabase.from('customers').select('id, name').order('name');
    setCustomers(data || []);
  }

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    setPendingCount(getPendingOrders(orgId).length);

    const handleOnline = () => {
      setIsOnline(true);
      syncPendingOrders();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncPendingOrders, orgId]);

  async function fetchProducts() {
    if (navigator.onLine) {
      const { data } = await supabase
        .from('products')
        .select('*')
        .eq('available', true)
        .order('category');
      if (data && data.length > 0) {
        cacheProducts(orgId, data);
        setProducts(data);
      }
    } else {
      setProducts(getCachedProducts(orgId).filter(p => p.available));
    }
    setLoading(false);
  }

  function addToCart(product) {
    const inCart = cart.find(i => i.id === product.id);
    const qtyInCart = inCart ? inCart.qty : 0;
    if (product.stock > 0 && qtyInCart >= product.stock) return;

    setCart(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) return prev.map(i => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...product, qty: 1 }];
    });
  }

  function removeFromCart(id) {
    setCart(prev => {
      const existing = prev.find(i => i.id === id);
      if (existing.qty === 1) return prev.filter(i => i.id !== id);
      return prev.map(i => i.id === id ? { ...i, qty: i.qty - 1 } : i);
    });
  }

  const total = cart.reduce((sum, i) => sum + i.price * i.qty, 0);

  const selectedCategory = category === 0 ? null : orgCategories[category - 1];
  const filtered = (selectedCategory === null ? products : products.filter(p => p.category === selectedCategory))
    .filter(p => p.stock === null || p.stock === undefined || p.stock > 0);

  const lowStock = products.filter(p => p.stock !== null && p.stock !== undefined && p.stock > 0 && p.stock <= 3);

  async function placeOrder() {
    if (cart.length === 0) return;

    // Carnet requires a customer (chosen or newly named)
    let carnetCustomerId = null;
    if (payment === 'carnet') {
      if (customerId) {
        carnetCustomerId = customerId;
      } else if (newCustomer.trim()) {
        setPlacing(true);
        const { data: created, error: custErr } = await supabase
          .from('customers')
          .insert([{ name: newCustomer.trim() }])
          .select()
          .single();
        if (custErr || !created) {
          setPlacing(false);
          setOrderError(true);
          setTimeout(() => setOrderError(false), 4000);
          return;
        }
        carnetCustomerId = created.id;
      } else {
        // No customer picked — block and highlight
        setOrderError(true);
        setTimeout(() => setOrderError(false), 4000);
        return;
      }
    }

    setPlacing(true);

    const order = {
      total,
      payment_method: payment,
      order_type: orderType,
      table_label: orderType === 'table' ? (tableLabel.trim() || null) : null,
      customer_id: carnetCustomerId,
      created_by: user?.id || null,
      status: 'completed',
    };

    const items = cart.map(i => ({
      product_id: i.id,
      product_name: i.name,
      price: i.price,
      cost: i.cost || 0,
      quantity: i.qty
    }));

    if (navigator.onLine) {
      const { data: savedOrder, error } = await supabase
        .from('orders')
        .insert([order])
        .select()
        .single();

      if (error || !savedOrder) {
        // Keep the cart so nothing is lost; surface a clear error
        setPlacing(false);
        setOrderError(true);
        setTimeout(() => setOrderError(false), 4000);
        return;
      }

      await supabase.from('order_items').insert(
        items.map(i => ({ ...i, order_id: savedOrder.id }))
      );

      for (const i of cart) {
        await supabase.rpc('decrement_stock', {
          product_id: i.id,
          amount: i.qty
        });
      }

      fetchProducts();
    } else {
      savePendingOrder(orgId, order, items);
      setPendingCount(getPendingOrders(orgId).length);
    }

    setCart([]);
    setTableLabel('');
    setCustomerId('');
    setNewCustomer('');
    if (carnetCustomerId) fetchCustomers();
    setPlacing(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2500);
  }

  if (loading) return <Loader text={tr.loadingMenu} />;

  return (
    <div className="order-screen">

      <div className={`status-bar ${isOnline ? 'online' : 'offline'}`}>
        {syncing ? tr.syncing :
          isOnline ? tr.online :
          `${tr.offline} — ${pendingCount} ${tr.pending}`}
      </div>

      {lowStock.length > 0 && (
        <div className="low-stock-warning">
          {tr.runningLow}: {lowStock.map(p => `${p.name} (${p.stock})`).join(', ')}
        </div>
      )}

      {success && (
        <div className="success-banner">
          {isOnline ? tr.orderSaved : tr.savedLocally}
        </div>
      )}

      {orderError && (
        <div className="error-banner">{tr.orderFailed}</div>
      )}

      <div className="categories">
        {CATEGORIES.map((c, idx) => (
          <button
            key={idx}
            className={`cat-btn ${category === idx ? 'active' : ''}`}
            onClick={() => setCategory(idx)}
          >{c}</button>
        ))}
      </div>

      <div className="products-grid">
        {filtered.map(p => (
          <button key={p.id} className="product-card" onClick={() => addToCart(p)}>
            <span className="product-name">{p.name}</span>
            <span className="product-price">{p.price} {tr.currency}</span>
            {p.stock !== null && p.stock !== undefined && p.stock <= 3 && (
              <span className="product-stock-low">⚠️ {p.stock} left</span>
            )}
          </button>
        ))}
      </div>

      {cart.length > 0 && (
        <div className="cart">
          <h3>{tr.currentOrder}</h3>
          {cart.map(i => (
            <div key={i.id} className="cart-item">
              <span className="cart-name">{i.name}</span>
              <div className="cart-controls">
                <button onClick={() => removeFromCart(i.id)}>−</button>
                <span>{i.qty}</span>
                <button onClick={() => addToCart(i)}>+</button>
              </div>
              <span className="cart-price">{i.price * i.qty} {tr.currency}</span>
            </div>
          ))}
          <div className="cart-total">
            <span>{tr.total}</span>
            <span>{total} {tr.currency}</span>
          </div>

          <div className="order-options">
            <span className="opt-label">{tr.orderType}</span>
            <div className="opt-chips">
              <button className={`opt-chip ${orderType === 'dine_in' ? 'active' : ''}`} onClick={() => setOrderType('dine_in')}>🍽️ {tr.dineIn}</button>
              <button className={`opt-chip ${orderType === 'takeaway' ? 'active' : ''}`} onClick={() => setOrderType('takeaway')}>🥡 {tr.takeaway}</button>
              <button className={`opt-chip ${orderType === 'table' ? 'active' : ''}`} onClick={() => setOrderType('table')}>🔢 {tr.table}</button>
            </div>
            {orderType === 'table' && (
              <input
                className="opt-table-input"
                type="text"
                inputMode="numeric"
                placeholder={tr.tableNumber}
                value={tableLabel}
                onChange={e => setTableLabel(e.target.value)}
              />
            )}

            <span className="opt-label">{tr.paymentMethod}</span>
            <div className="opt-chips">
              <button className={`opt-chip ${payment === 'cash' ? 'active' : ''}`} onClick={() => setPayment('cash')}>💵 {tr.payCash}</button>
              <button className={`opt-chip ${payment === 'card' ? 'active' : ''}`} onClick={() => setPayment('card')}>💳 {tr.payCard}</button>
              {isPro && (
                <button className={`opt-chip ${payment === 'carnet' ? 'active' : ''}`} onClick={() => setPayment('carnet')}>📒 {tr.payCarnet}</button>
              )}
            </div>

            {payment === 'carnet' && (
              <div className="carnet-picker">
                <select
                  className="opt-table-input"
                  value={customerId}
                  onChange={e => { setCustomerId(e.target.value); setNewCustomer(''); }}
                >
                  <option value="">— {tr.chooseCustomer} —</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {!customerId && (
                  <input
                    className="opt-table-input"
                    type="text"
                    placeholder={tr.orNewCustomer}
                    value={newCustomer}
                    onChange={e => setNewCustomer(e.target.value)}
                  />
                )}
              </div>
            )}
          </div>

          <button className="place-btn" onClick={placeOrder} disabled={placing}>
            {placing ? tr.saving : `${tr.confirmOrder} — ${total} ${tr.currency}`}
          </button>
        </div>
      )}
    </div>
  );
}

export default OrderScreen;